import { Directive, OnInit, effect, inject, input, output } from '@angular/core';
import { first } from 'rxjs/operators';
import { AccountContextService, StrategyStyleType } from '../services/account-context.service';
import { Account } from '../../../domain/account.domain';

@Directive({
  selector: '[tdWbAccountContext]'
})
export class AccountContextDirective implements OnInit {
  private readonly accountContextService = inject(AccountContextService);

  readonly initAccountId = input<string | undefined>();
  readonly strategyStyle = input<StrategyStyleType>('default', { alias: 'tdWbAccountContextType' });
  readonly accountContextUpdate = input<boolean | undefined>(undefined, { alias: 'tdWbAccountContextUpdate' });
  readonly groupOverridesAccountContext = input<boolean | undefined>(undefined, { alias: 'tdWbAccountContextGroupOverride' });

  // Inputs/Outputs of account selector
  readonly accounts = input<Array<Account> | undefined>();
  readonly account = input<Account | undefined>();
  readonly accountChange = output<Account>();

  constructor() {
    // effect: propagates account input changes to the account-context service —
    // this directive uses effect() as a reactive alternative to implementing OnChanges.
    effect(() => {
      const account = this.account();
      // Guard against both undefined and null to prevent null propagation to service.
      if (account != null) {
        this.storeAccountAndGroupId(account);
      }
    });
  }

  ngOnInit(): void {
    this.initSelectedAccount();
  }

  private getSelectedAccountGroupId(): string {
    return this.accountContextService.getSelectedAccountGroupId();
  }

  private storeAccountAndGroupId(account: Account): void {
    if (this.accountContextUpdate() && account?.id !== undefined && account.groupId !== undefined) {
      this.accountContextService.setSelectedAccountId(account.id, this.strategyStyle());
      this.accountContextService.setSelectedAccountGroupId(account.groupId);
    }
  }

  private initSelectedAccount(): void {
    this.accountContextService.resolveAccount(this.accounts() ?? [], this.initAccountId(), this.strategyStyle())
      .pipe(first())
      .subscribe((account: Account): void => {
        let selectedGroupId;
        if (account !== undefined) {
          if (this.groupOverridesAccountContext()) {
            selectedGroupId = this.getSelectedAccountGroupId();
            const accs = this.accounts();
            if (accs && account.groupId !== selectedGroupId) {
              account = accs.reduceRight((previous: Account, current: Account): Account => {
                const matchingGroupId = current.groupId === this.getSelectedAccountGroupId();
                return matchingGroupId ? current : previous;
              }, accs[0]);
            }
          }
          this.storeAccountAndGroupId(account);
          this.accountChange.emit(account);
        }
      });
  }
}
